import { ReplaySubject, Observable, throwError, Subscription } from 'rxjs';
import { take, timeout, catchError, filter } from 'rxjs/operators';
import { Logger } from './logger';
import { IParameterService } from '../interfaces/parameter-service';
import { Component, OnDestroy } from '@angular/core';

/**
 * Clase base para el manejo de parámetros.
 * Provee metodos para la obtención de parámetros y la modificación de éste.
 */
@Component({
  template: ''
})
export class Parameter<T> extends Logger implements IParameterService<T>, OnDestroy {

  protected alreadyStarted = false;
  protected sourceSubject: ReplaySubject<T>;
  protected subscription: Subscription;
  protected $target: T;
  protected get target(): T {
    return this.$target;
  }
  protected set target(value: T) {
    this.$target = value;
    this.sourceSubject.next(value);
  }

  constructor() {
    super();
    this.sourceSubject = new ReplaySubject(1);
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Inicializa el parámetro retornando una promesa que se resolverá con el
   * valor del elemento actual cuando éste exista.
   */
  public init(): Promise<T> {
    this.alreadyStarted = true;
    this.start();
    return new Promise((resolve) => {
      this.sourceSubject.pipe(take(1)).subscribe(
        (arg0: T) => {
          resolve(arg0);
        },
      );
    });
  }

  /**
   * Retorna el valor del parámetro actual
   */
  public get(): T {
    return this.target;
  }

  /**
   * Retorna el valor del parámetro cuando el valor exista.
   * @param maxAwait tiempo de espera máximo para esperar el valor.
   *
   * Debe se representado en milisegundos.
   */
  public getAsync(maxAwait?: number): Promise<T> {
    return new Promise((resolve) => {
      this.sourceSubject.pipe(
        take(1),
        timeout(maxAwait || 0),
        catchError(
          (error: Error) => {
            resolve(null);
            return throwError(error);
          },
        ),
      ).subscribe(
        (arg0: T) => resolve(arg0)
      );
    });
  }

  /**
   * Modifica el valor del parámetro actual
   * @param value valor para modificar
   */
  public set(value: T): void {
    this.target = value;
  }

   /**
    * Método que deberá generar la emisión de valores para el obervable
    * de la clase
    */
  public start(): Promise<T> {
    return new Promise((resolve) => resolve());
  }

  /**
   * Retorna una referencia al observable de la clase
   */
  public getObservable(): Observable<T> {
    return this.sourceSubject.asObservable();
  }

  /**
   * Retorna ``true`` si el servicio ya se inicializó
   */
  public isAlreadyStarted(): boolean {
    return this.alreadyStarted;
  }

  /**
   * Returns ``true`` if the service has a valid parameter value
   */
  public hasValue(): boolean {
    return this.isAlreadyStarted() && !!this.get();
  }

  /**
   * Look at the observable for a change
   * @param source Observable value
   */
  public startWhen(source: Observable<any>): void {
    this.subscription = source.pipe(
      filter((data) => !!data),
    ).subscribe(() => this.start());
  }
}
